import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button } from '@arabquiz/ui'
import { Loader2 } from 'lucide-react'
import { Modal } from '../question-bank/Modal'
import { questionBankKeys, type BankCategory } from '../question-bank/types'

type RandomQuizPickerDialogProps = {
  open: boolean
  onClose: () => void
}

type Scope = 'Public' | 'Private' | 'Both'

const COUNT_PRESETS = [5, 10, 20, 30, 50] as const

const SCOPE_OPTIONS: { value: Scope; labelKey: 'randomQuizScopePublic' | 'randomQuizScopePrivate' | 'randomQuizScopeBoth' }[] = [
  { value: 'Public', labelKey: 'randomQuizScopePublic' },
  { value: 'Private', labelKey: 'randomQuizScopePrivate' },
  { value: 'Both', labelKey: 'randomQuizScopeBoth' },
]

const DIFFICULTY_OPTIONS = [
  { value: 'Easy', labelKey: 'bankDifficultyEasy' },
  { value: 'Medium', labelKey: 'bankDifficultyMedium' },
  { value: 'Hard', labelKey: 'bankDifficultyHard' },
] as const

export function RandomQuizPickerDialog({ open, onClose }: RandomQuizPickerDialogProps) {
  const { t } = useTranslation('quiz')
  const countInputId = useId()
  const timeInputId = useId()

  const [selectedPreset, setSelectedPreset] = useState<number>(20)
  const [customCount, setCustomCount] = useState<string>('')
  const [scope, setScope] = useState<Scope>('Public')
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [difficulties, setDifficulties] = useState<string[]>([])
  const [timeOverride, setTimeOverride] = useState<string>('')

  const effectiveCount = customCount !== '' ? parseInt(customCount, 10) : selectedPreset
  const isCountValid = Number.isInteger(effectiveCount) && effectiveCount >= 1 && effectiveCount <= 50

  const customParsed = customCount !== '' ? parseInt(customCount, 10) : NaN
  const customCountOutOfBounds =
    customCount !== '' &&
    (!Number.isInteger(customParsed) || customParsed < 1 || customParsed > 50)

  const timeParsed = timeOverride !== '' ? parseInt(timeOverride, 10) : NaN
  const timeOutOfBounds =
    timeOverride !== '' &&
    (!Number.isInteger(timeParsed) || timeParsed < 5 || timeParsed > 120)

  const categoriesQuery = useQuery({
    queryKey: questionBankKeys.categories(),
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/question-bank/categories', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) throw new Error('categories load failed')
      return res.json() as Promise<BankCategory[]>
    },
    enabled: open,
  })

  const categories = categoriesQuery.data ?? []

  function toggleCategory(id: string) {
    setCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    )
  }

  function toggleDifficulty(value: string) {
    setDifficulties((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value],
    )
  }

  const mutation = useMutation({
    mutationFn: async () => {
      // exactOptionalPropertyTypes: never assign undefined directly to an
      // optional prop — only assign array/number props when non-empty/valid.
      const body: {
        count: number
        scope: string
        categoryIds?: string[]
        difficulties?: string[]
        timeSecondsOverride?: number
      } = { count: effectiveCount, scope }
      if (categoryIds.length > 0) body.categoryIds = categoryIds
      if (difficulties.length > 0) body.difficulties = difficulties
      if (timeOverride !== '' && !timeOutOfBounds) body.timeSecondsOverride = parseInt(timeOverride, 10)

      const res = await fetch('/api/v1/games/random', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null
        throw new Error(data?.error ?? 'unknown')
      }
      return res.json() as Promise<{ id: string; pin: string }>
    },
    onSuccess: (game) => {
      window.location.assign(`/games/host/${encodeURIComponent(game.id)}`)
    },
  })

  function resolveError(msg: string): string {
    if (msg === 'EmptyPool') return t('randomQuizErrorEmptyPool')
    if (msg === 'RateLimitExceeded') return t('randomQuizErrorRateLimited')
    return t('randomQuizError')
  }

  const submitDisabled =
    categoriesQuery.isLoading || !isCountValid || timeOutOfBounds || mutation.isPending

  const chipBase =
    'rounded-xl border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 '
  const chipActive = 'border-brand-300 bg-brand-50 text-brand-800 ring-1 ring-brand-200'
  const chipInactive =
    'border-brand-200 bg-white text-brand-700 hover:border-brand-300 hover:bg-brand-50/40'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('randomQuizDialogTitle')}
      description={t('randomQuizDialogDescription')}
    >
      <div className="space-y-6">
        {/* Count */}
        <fieldset>
          <legend className="block text-sm font-semibold text-brand-900 mb-2">
            {t('randomQuizCount')}
          </legend>
          <div role="group" aria-label={t('randomQuizCount')} className="flex flex-wrap gap-2">
            {COUNT_PRESETS.map((preset) => {
              const active = customCount === '' && selectedPreset === preset
              return (
                <button
                  key={preset}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setSelectedPreset(preset)
                    setCustomCount('')
                  }}
                  className={chipBase + (active ? chipActive : chipInactive)}
                >
                  {preset}
                </button>
              )
            })}
          </div>
          <div className="mt-3">
            <label
              htmlFor={countInputId}
              className="block text-xs font-medium text-brand-600 mb-1.5"
            >
              {t('randomQuizCustomCount')}
            </label>
            <input
              id={countInputId}
              type="number"
              min={1}
              max={50}
              dir="ltr"
              value={customCount}
              onChange={(e) => {
                setCustomCount(e.target.value)
                if (e.target.value !== '') setSelectedPreset(0)
              }}
              placeholder={t('randomQuizCustomCountPlaceholder')}
              className="h-10 w-32 rounded-md border border-brand-200 bg-white ps-3 pe-3 text-sm text-brand-900 placeholder:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            />
            {customCountOutOfBounds ? (
              <p className="mt-1.5 text-xs text-red-600" role="alert">
                {t('randomQuizErrorCountBounds', { min: 1, max: 50 })}
              </p>
            ) : null}
          </div>
        </fieldset>

        {/* Scope */}
        <fieldset>
          <legend className="block text-sm font-semibold text-brand-900 mb-2">
            {t('randomQuizScopeLabel')}
          </legend>
          <div role="group" aria-label={t('randomQuizScopeLabel')} className="flex flex-wrap gap-2">
            {SCOPE_OPTIONS.map(({ value, labelKey }) => {
              const active = scope === value
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setScope(value)}
                  className={chipBase + (active ? chipActive : chipInactive)}
                >
                  {t(labelKey)}
                </button>
              )
            })}
          </div>
        </fieldset>

        {/* Category */}
        <fieldset>
          <legend className="block text-sm font-semibold text-brand-900 mb-2">
            {t('randomQuizCategoryLabel')}
          </legend>
          <p className="text-xs text-brand-600 mb-2">{t('randomQuizCategoryHint')}</p>
          {/* Chips, not a listbox: a small curated set shows all options at a glance and
              reuses the dialog's chip styling. If the live bank ever exposes a very large
              number of categories (say > ~25), wrap this group in `max-h-48 overflow-y-auto`
              rather than switching to a listbox — keep the chip interaction model. */}
          <div
            role="group"
            aria-label={t('randomQuizCategoryLabel')}
            className="flex flex-wrap gap-2"
          >
            {categoriesQuery.isLoading ? (
              <button
                type="button"
                disabled
                aria-pressed={categoryIds.length === 0}
                className={chipBase + chipActive + ' opacity-50'}
              >
                ...
              </button>
            ) : (
              <>
                <button
                  type="button"
                  aria-pressed={categoryIds.length === 0}
                  onClick={() => setCategoryIds([])}
                  className={chipBase + (categoryIds.length === 0 ? chipActive : chipInactive)}
                >
                  {t('randomQuizAllCategories')}
                </button>
                {categories.map((category) => {
                  const active = categoryIds.includes(category.id)
                  return (
                    <button
                      key={category.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleCategory(category.id)}
                      className={chipBase + (active ? chipActive : chipInactive)}
                    >
                      {category.name}
                    </button>
                  )
                })}
              </>
            )}
          </div>
        </fieldset>

        {/* Difficulty */}
        <fieldset>
          <legend className="block text-sm font-semibold text-brand-900 mb-2">
            {t('randomQuizDifficultyLabel')}
          </legend>
          <p className="text-xs text-brand-600 mb-2">{t('randomQuizDifficultyHint')}</p>
          <div role="group" aria-label={t('randomQuizDifficultyLabel')} className="flex flex-wrap gap-2">
            <button
              type="button"
              aria-pressed={difficulties.length === 0}
              onClick={() => setDifficulties([])}
              className={chipBase + (difficulties.length === 0 ? chipActive : chipInactive)}
            >
              {t('randomQuizAllDifficulties')}
            </button>
            {DIFFICULTY_OPTIONS.map(({ value, labelKey }) => {
              const active = difficulties.includes(value)
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleDifficulty(value)}
                  className={chipBase + (active ? chipActive : chipInactive)}
                >
                  {t(labelKey)}
                </button>
              )
            })}
          </div>
        </fieldset>

        {/* Time override */}
        <fieldset>
          <legend className="block text-sm font-semibold text-brand-900 mb-2">
            {t('randomQuizTimeLabel')}
          </legend>
          <div className="inline-flex items-center gap-2">
            <input
              id={timeInputId}
              type="number"
              min={5}
              max={120}
              step={5}
              dir="ltr"
              value={timeOverride}
              onChange={(e) => setTimeOverride(e.target.value)}
              placeholder={t('randomQuizTimePlaceholder')}
              className="h-10 w-24 rounded-md border border-brand-200 bg-white ps-3 pe-3 text-sm text-brand-900 placeholder:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            />
            <span className="text-sm text-brand-700">{t('randomQuizTimeUnit')}</span>
          </div>
          <p className="mt-2 text-xs text-brand-600">{t('randomQuizTimeHint')}</p>
          {timeOutOfBounds ? (
            <p className="mt-1.5 text-xs text-red-600" role="alert">
              {t('randomQuizErrorTimeBounds', { min: 5, max: 120 })}
            </p>
          ) : null}
        </fieldset>

        {/* Error */}
        {mutation.isError ? (
          <div
            className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
            role="alert"
          >
            {resolveError(mutation.error.message)}
          </div>
        ) : null}

        {/* CTA */}
        <Button
          type="button"
          size="lg"
          className="w-full gap-2"
          disabled={submitDisabled}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              <span>{t('randomQuizPreparing')}</span>
            </>
          ) : (
            <span>{t('randomQuizStart')}</span>
          )}
        </Button>
      </div>
    </Modal>
  )
}
